// =============================================================================
// UPCOMING EVENTS — Tabelle der nächsten Auftritte
// -----------------------------------------------------------------------------
// Liest Termine live aus dem Google Sheet (CSV-URL in config.js).
// Spalten erwartet: date (YYYY-MM-DD), event, location
// Logik:
//   - Termine in der Vergangenheit werden ausgeblendet
//   - Sortierung aufsteigend nach Datum (nächstes Datum oben)
//   - Maximal EVENTS_MAX (Default: 10) Einträge
// =============================================================================

import { useEffect, useState } from "react";
import { EVENTS_CSV_URL, EVENTS_MAX } from "@/config";
import { parseCsv, formatDateDe } from "@/utils/csv";

export default function UpcomingEvents() {
  // null = lädt noch, [] = keine Events, [...] = Daten geladen
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    fetch(EVENTS_CSV_URL, { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error("fetch failed");
        return r.text();
      })
      .then((text) => {
        if (cancelled) return;

        const rows = parseCsv(text).filter((r) => r.some((c) => c && c.trim()));
        if (!rows.length) {
          setEvents([]);
          return;
        }

        // Spalten-Index anhand der Kopfzeile finden
        const header = rows[0].map((h) => h.trim().toLowerCase());
        const idxDate = header.indexOf("date");
        const idxEvent = header.indexOf("event");
        const idxLocation = header.indexOf("location");

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const parsed = rows
          .slice(1)
          .map((r) => ({
            date: (r[idxDate] || "").trim(),
            event: (r[idxEvent] || "").trim(),
            location: (r[idxLocation] || "").trim(),
          }))
          .filter((e) => e.date && e.event)
          // Vergangene Events ausblenden
          .filter((e) => {
            const d = new Date(e.date + "T00:00:00");
            return !isNaN(d) && d >= today;
          })
          // Aufsteigend nach Datum sortieren
          .sort((a, b) => a.date.localeCompare(b.date))
          // Auf maximale Anzahl begrenzen
          .slice(0, EVENTS_MAX);

        setEvents(parsed);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="section-block reveal d3" data-testid="section-events">
      <h2 className="section-title">Upcoming Events</h2>

      {/* Ladezustand */}
      {events === null && !error && (
        <div className="events-state" data-testid="events-loading">
          Lade Termine …
        </div>
      )}

      {/* Fehlerzustand (z.B. Sheet nicht erreichbar) */}
      {error && (
        <div className="events-state" data-testid="events-error">
          Termine konnten gerade nicht geladen werden.
        </div>
      )}

      {/* Keine Events vorhanden */}
      {events && events.length === 0 && (
        <div className="events-state" data-testid="events-empty">
          Bald wieder neue Termine.
        </div>
      )}

      {/* Tabelle mit Events */}
      {events && events.length > 0 && (
        <div className="events-table" role="table" data-testid="events-table">
          {events.map((e, i) => (
            <div
              className="events-row"
              role="row"
              key={`${e.date}-${i}`}
              data-testid={`event-row-${i}`}
            >
              <span role="cell" className="events-date">
                {formatDateDe(e.date)}
              </span>
              <span role="cell" className="events-name">
                {e.event}
              </span>
              <span role="cell" className="events-loc">
                {e.location}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
